import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CapsuleButton } from "../_components/CapsuleButton";
import { CustomModal } from "../_components/CustomModal";
import { CaptionText, SimpleToggle } from "../_components";
import { IsoRangeDatePicker } from "../_components/IsoRangeDatePicker";
import { parkingActions } from "../_actions";
import { Selectors } from "../_reducers/app.reducer";
import { MainPageWithButtons } from "../_components";
import { TimeHelper } from "../_helpers";
import {
    resolveAssignmentsVisibility,
    resolveParkingPermissions,
} from "./_helpers/parkingPermissions";

export function ParkingSpotConfigView() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const spot = useSelector(Selectors.getSelectedParkingSpot);
    const locationInfo = useSelector(Selectors.getLocationInfo);
    const currentUserEmail = useSelector(Selectors.getUserEmail);
    const { canManage, canManageAssignments } = resolveParkingPermissions({
        locationPermission: locationInfo?.locationPermission,
        currentUserEmail,
        spotHolderEmail: spot?.holderEmail,
    });
    const assignmentsVisibility = resolveAssignmentsVisibility({
        canManageAssignments,
        spotHolderEmail: spot?.holderEmail,
        spotHolderUserUid: spot?.holderUserUid,
        assignmentsCount: spot?.assignments.length ?? 0,
        maxAssignments: spot?.maxAssignments ?? 0,
    });

    const [holderName, setHolderName] = useState(spot?.holderName ?? "");
    const [holderEmail, setHolderEmail] = useState(spot?.holderEmail ?? "");
    const [maxAssignments, setMaxAssignments] = useState(
        String(spot?.maxAssignments ?? 1),
    );
    const [notes, setNotes] = useState(spot?.notes ?? "");
    const [newAssignmentName, setNewAssignmentName] = useState("");
    const [newAssignmentEmail, setNewAssignmentEmail] = useState("");
    const [isAssignmentTemporary, setIsAssignmentTemporary] = useState(false);
    const [newAssignmentBegin, setNewAssignmentBegin] = useState<string | null>(
        null,
    );
    const [newAssignmentEnd, setNewAssignmentEnd] = useState<string | null>(
        null,
    );
    const [assignmentError, setAssignmentError] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [assignmentToRemove, setAssignmentToRemove] = useState<
        { email: string; userName: string | null } | null
    >(null);

    useEffect(() => {
        setHolderName(spot?.holderName ?? "");
        setHolderEmail(spot?.holderEmail ?? "");
        setMaxAssignments(String(spot?.maxAssignments ?? 1));
        setNotes(spot?.notes ?? "");
        setNewAssignmentName("");
        setNewAssignmentEmail("");
        setIsAssignmentTemporary(false);
        setNewAssignmentBegin(null);
        setNewAssignmentEnd(null);
        setAssignmentError(null);
    }, [
        spot?.uid,
        spot?.holderName,
        spot?.holderEmail,
        spot?.maxAssignments,
        spot?.notes,
    ]);

    if (!spot) return null;

    const parsedMax = parseInt(maxAssignments, 10);
    const safeMax =
        Number.isFinite(parsedMax) && parsedMax >= 0 ? parsedMax : 1;

    const isDirty =
        (spot.holderName ?? "") !== holderName ||
        (spot.holderEmail ?? "") !== holderEmail ||
        spot.maxAssignments !== safeMax ||
        (spot.notes ?? "") !== notes;

    const handleSave = () => {
        dispatch(
            parkingActions.updateSpot({
                spotUid: spot.uid,
                holderName: holderName.trim() || null,
                holderEmail: holderEmail.trim() || null,
                maxAssignments: safeMax,
                notes,
            }),
        );
    };

    const handleDelete = () => {
        setShowDeleteConfirm(false);
        dispatch(parkingActions.deleteSpot(spot.uid));
    };

    const handleAddAssignment = async () => {
        const email = newAssignmentEmail.trim();
        const name = newAssignmentName.trim();
        if (!email || !spot) return;
        if (isAssignmentTemporary) {
            if (!newAssignmentBegin || !newAssignmentEnd) {
                setAssignmentError(
                    t("mod_parking_assignment_date_range_required"),
                );
                return;
            }
            if (
                new Date(newAssignmentBegin).getTime() >=
                new Date(newAssignmentEnd).getTime()
            ) {
                setAssignmentError(
                    t("mod_parking_assignment_date_range_invalid"),
                );
                return;
            }
            if (new Date(newAssignmentEnd).getTime() <= Date.now()) {
                setAssignmentError(
                    t("mod_parking_assignment_date_range_past"),
                );
                return;
            }
        }
        setAssignmentError(null);
        const result = (await dispatch(
            parkingActions.addAssignment({
                spotUid: spot.uid,
                email,
                userName: name || email.split("@")[0],
                begin: isAssignmentTemporary ? newAssignmentBegin : null,
                end: isAssignmentTemporary ? newAssignmentEnd : null,
            }),
        )) as unknown as { ok: boolean; error?: string; limit?: number };
        if (result.ok) {
            setNewAssignmentEmail("");
            setNewAssignmentName("");
            setIsAssignmentTemporary(false);
            setNewAssignmentBegin(null);
            setNewAssignmentEnd(null);
        } else if (result.error === "LIMIT_REACHED") {
            setAssignmentError(
                t("mod_parking_assignment_limit_reached", {
                    limit: result.limit ?? spot.maxAssignments,
                }),
            );
        } else if (result.error === "DUPLICATE") {
            setAssignmentError(t("mod_parking_assignment_duplicate"));
        } else if (result.error === "TEMP_NOT_APPLICABLE") {
            setAssignmentError(
                t("mod_parking_assignment_temporary_not_applicable"),
            );
        } else {
            setAssignmentError(t("mod_parking_assignment_error"));
        }
    };

    const handleRemoveAssignment = (email: string) => {
        dispatch(
            parkingActions.removeAssignment({
                spotUid: spot.uid,
                email,
            }),
        );
    };

    return (
        <>
            <MainPageWithButtons
                pageTitle={`${t("mod_parking_spot")} ${spot.number}`}
                pageSubtitle={`${t("mod_parking_floor")} ${spot.floor}`}
                size={"MEDIUM"}
                content={
                    <form
                        autoComplete="off"
                        onSubmit={(e) => e.preventDefault()}
                        // px-1 avoids the bootstrap focus box-shadow on inputs
                        // getting clipped by the surrounding container edges.
                        className="px-1 py-2"
                    >
                        {spot.warnings.length > 0 && (
                            <div
                                className="rounded p-2 mb-3"
                                style={{
                                    backgroundColor: "#fff3cd",
                                    border: "1px solid #ffeeba",
                                }}
                            >
                                <div className="font-weight-bold small mb-1">
                                    {t("mod_parking_warnings_title")}
                                </div>
                                <ul className="mb-0 pl-3 small">
                                    {spot.warnings.map((w, i) => (
                                        <li key={i}>
                                            {t(
                                                `mod_parking_warning_${w.code}`,
                                                {
                                                    defaultValue:
                                                        w.message || w.code,
                                                },
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {canManage && (
                            <div className="form-group">
                                <label className="font-weight-bold mb-1">
                                    {t("mod_parking_holder")}{" "}
                                    <small className="text-muted">
                                        ({t("global_optional")})
                                    </small>
                                </label>
                                <input
                                    type="text"
                                    name="parking-edit-holder-name"
                                    autoComplete="off"
                                    className="form-control form-control-sm mb-2"
                                    value={holderName}
                                    onChange={(e) =>
                                        setHolderName(e.target.value)
                                    }
                                    placeholder={t(
                                        "mod_parking_new_holder_name_placeholder",
                                    )}
                                />
                                <input
                                    type="email"
                                    name="parking-edit-holder-email"
                                    autoComplete="off"
                                    className="form-control form-control-sm"
                                    value={holderEmail}
                                    onChange={(e) =>
                                        setHolderEmail(e.target.value)
                                    }
                                    placeholder={t(
                                        "mod_parking_new_holder_email_placeholder",
                                    )}
                                />
                                {!holderName.trim() && (
                                    <CaptionText className="font-italic mt-1">
                                        {t("mod_parking_empty_holder_means_free")}
                                    </CaptionText>
                                )}
                            </div>
                        )}

                        {canManage ? (
                            <div className="form-group">
                                <label className="font-weight-bold mb-1">
                                    {t("mod_parking_max_assignments")}
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    name="parking-edit-max"
                                    autoComplete="off"
                                    className="form-control form-control-sm"
                                    value={maxAssignments}
                                    onChange={(e) =>
                                        setMaxAssignments(e.target.value)
                                    }
                                />
                                {spot.maxAssignments > 0 && (
                                    <small className="text-muted">
                                        {spot.assignments.length} /{" "}
                                        {spot.maxAssignments}
                                    </small>
                                )}
                            </div>
                        ) : spot.maxAssignments > 0 ? (
                            <div className="mb-3">
                                <div className="font-weight-bold">
                                    {t("mod_parking_max_assignments")}
                                </div>
                                <div>
                                    {spot.assignments.length} /{" "}
                                    {spot.maxAssignments}
                                </div>
                            </div>
                        ) : null}

                        {canManage ? (
                            <div className="form-group">
                                <label className="font-weight-bold mb-1">
                                    {t("mod_parking_notes")}{" "}
                                    <small className="text-muted">
                                        ({t("global_optional")})
                                    </small>
                                </label>
                                <textarea
                                    name="parking-edit-notes"
                                    autoComplete="off"
                                    className="form-control form-control-sm"
                                    rows={3}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </div>
                        ) : spot.notes ? (
                            <div className="mb-3">
                                <div className="font-weight-bold">
                                    {t("mod_parking_notes")}
                                </div>
                                <div className="text-break">{spot.notes}</div>
                            </div>
                        ) : null}

                        {/* Assignments section — visibility is computed by
                            `resolveAssignmentsVisibility`. See helper for the rules. */}
                        {assignmentsVisibility.showSection && (
                            <div className="form-group mt-4 pt-3 border-top">
                                {spot.assignments.length > 0 && (
                                    <label className="font-weight-bold mb-1">
                                        {t("mod_parking_assignments")}
                                    </label>
                                )}
                                {spot.assignments.length > 0 && (
                                    <ul className="list-unstyled mb-2">
                                        {spot.assignments.map((a) => (
                                            <li
                                                key={a.email}
                                                className="d-flex align-items-center justify-content-between c-bg-grey-250 c-bd-220 rounded px-3 py-2 mb-2"
                                            >
                                                <div className="text-break mr-2">
                                                    {a.userName && (
                                                        <div className="font-weight-bold">
                                                            {a.userName}
                                                        </div>
                                                    )}
                                                    <div>{a.email}</div>
                                                    {(a.begin || a.end) && (
                                                        <CaptionText className="font-italic mt-1">
                                                            {a.begin && a.end
                                                                ? t(
                                                                      "mod_parking_assignment_valid_range",
                                                                      {
                                                                          from: TimeHelper.localizeIsoStringToFormat(
                                                                              a.begin,
                                                                              locationInfo?.timezone,
                                                                              "DD MMM YYYY",
                                                                          ),
                                                                          to: TimeHelper.localizeIsoStringToFormat(
                                                                              a.end,
                                                                              locationInfo?.timezone,
                                                                              "DD MMM YYYY",
                                                                          ),
                                                                      },
                                                                  )
                                                                : a.begin
                                                                ? t(
                                                                      "mod_parking_assignment_valid_from",
                                                                      {
                                                                          from: TimeHelper.localizeIsoStringToFormat(
                                                                              a.begin,
                                                                              locationInfo?.timezone,
                                                                              "DD MMM YYYY",
                                                                          ),
                                                                      },
                                                                  )
                                                                : t(
                                                                      "mod_parking_assignment_valid_until",
                                                                      {
                                                                          to: TimeHelper.localizeIsoStringToFormat(
                                                                              a.end,
                                                                              locationInfo?.timezone,
                                                                              "DD MMM YYYY",
                                                                          ),
                                                                      },
                                                                  )}
                                                        </CaptionText>
                                                    )}
                                                    {a.warnings.map((w, i) => (
                                                        <div
                                                            key={i}
                                                            className="small text-warning mt-1"
                                                        >
                                                            ⚠️{" "}
                                                            {t(
                                                                `mod_parking_warning_${w.code}`,
                                                                {
                                                                    defaultValue:
                                                                        w.message ||
                                                                        w.code,
                                                                },
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <CapsuleButton
                                                    size="SMALL"
                                                    style="DANGER"
                                                    text={t(
                                                        "mod_parking_assignment_remove",
                                                    )}
                                                    onClick={() =>
                                                        setAssignmentToRemove({
                                                            email: a.email,
                                                            userName: a.userName,
                                                        })
                                                    }
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {assignmentsVisibility.showHolderNotAcceptedBanner && (
                                    <div
                                        className="rounded px-3 py-2 mb-0"
                                        style={{
                                            backgroundColor: "#fff3cd",
                                            border: "1px solid #ffeeba",
                                        }}
                                    >
                                        <div className="small">
                                            {t(
                                                "mod_parking_assignment_holder_not_accepted",
                                            )}
                                        </div>
                                    </div>
                                )}
                                {assignmentsVisibility.showAddForm && (
                                    <div className="c-bg-grey-250 c-bd-220 rounded p-3 mt-3">
                                        <h6 className="font-weight-bold mb-3">
                                            {t("mod_parking_assignment_new")}
                                        </h6>
                                        <div className="form-group mb-2">
                                            <label className="small text-muted mb-1">
                                                {t("mod_parking_assignment_name_label")}
                                            </label>
                                            <input
                                                type="text"
                                                name="parking-new-assignment-name"
                                                autoComplete="off"
                                                className="form-control form-control-sm"
                                                placeholder={t(
                                                    "mod_parking_assignment_name_placeholder_short",
                                                )}
                                                value={newAssignmentName}
                                                onChange={(e) =>
                                                    setNewAssignmentName(
                                                        e.target.value,
                                                    )
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key !== "Enter")
                                                        return;
                                                    e.preventDefault();
                                                    if (
                                                        newAssignmentEmail
                                                            .trim().length > 0
                                                    ) {
                                                        handleAddAssignment();
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="form-group mb-3">
                                            <label className="small text-muted mb-1">
                                                {t("mod_parking_assignment_email_label")}
                                            </label>
                                            <input
                                                type="email"
                                                name="parking-new-assignment-email"
                                                autoComplete="off"
                                                className="form-control form-control-sm"
                                                placeholder={t(
                                                    "mod_parking_assignment_email_placeholder_short",
                                                )}
                                                value={newAssignmentEmail}
                                                onChange={(e) => {
                                                    setNewAssignmentEmail(
                                                        e.target.value,
                                                    );
                                                    setAssignmentError(null);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key !== "Enter")
                                                        return;
                                                    // Prevent implicit form
                                                    // submission, which would
                                                    // click the first <button>
                                                    // in the form (a Remove
                                                    // button) and wipe an
                                                    // assignment.
                                                    e.preventDefault();
                                                    if (
                                                        newAssignmentEmail
                                                            .trim().length > 0
                                                    ) {
                                                        handleAddAssignment();
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="form-group mb-3">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <label className="small text-muted mb-0">
                                                    {t(
                                                        "mod_parking_assignment_temporary",
                                                    )}
                                                </label>
                                                <SimpleToggle
                                                    size="SMALL"
                                                    checked={isAssignmentTemporary}
                                                    handleChange={({
                                                        detail: { value },
                                                    }) => {
                                                        setIsAssignmentTemporary(
                                                            value,
                                                        );
                                                        setAssignmentError(null);
                                                        if (value) {
                                                            if (!newAssignmentBegin) {
                                                                setNewAssignmentBegin(
                                                                    TimeHelper.getTodayIsoString(),
                                                                );
                                                            }
                                                            if (!newAssignmentEnd) {
                                                                setNewAssignmentEnd(
                                                                    TimeHelper.getTomorrowIsoString(),
                                                                );
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                            {isAssignmentTemporary && (
                                                <IsoRangeDatePicker
                                                    timezone={
                                                        locationInfo?.timezone
                                                    }
                                                    begin={newAssignmentBegin}
                                                    end={newAssignmentEnd}
                                                    minDate={new Date()}
                                                    onChangeStart={(date) => {
                                                        setNewAssignmentBegin(
                                                            date
                                                                ? date.toISOString()
                                                                : null,
                                                        );
                                                        setAssignmentError(null);
                                                    }}
                                                    onChangeEnd={(date) => {
                                                        setNewAssignmentEnd(
                                                            date
                                                                ? date.toISOString()
                                                                : null,
                                                        );
                                                        setAssignmentError(null);
                                                    }}
                                                />
                                            )}
                                        </div>
                                        {assignmentError && (
                                            <div className="text-danger small mb-2">
                                                {assignmentError}
                                            </div>
                                        )}
                                        <div className="d-flex justify-content-end">
                                            <CapsuleButton
                                                text={t("mod_parking_assign")}
                                                isEnabled={
                                                    newAssignmentEmail.trim()
                                                        .length > 0
                                                }
                                                onClick={handleAddAssignment}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {canManage && (
                            <div className="d-flex flex-wrap align-items-center mt-4 pt-3 border-top">
                                <CapsuleButton
                                    text={t("global_save")}
                                    isEnabled={isDirty}
                                    onClick={handleSave}
                                />
                                <div className="ml-auto">
                                    <CapsuleButton
                                        style="DANGER"
                                        text={t("mod_parking_delete")}
                                        onClick={() =>
                                            setShowDeleteConfirm(true)
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </form>
                }
            />
            <CustomModal
                show={!!assignmentToRemove}
                centered
                onHide={() => setAssignmentToRemove(null)}
                title={t("mod_parking_assignment_remove_title")}
                content={
                    assignmentToRemove && (
                        <div>
                            <p>
                                {t("mod_parking_assignment_remove_confirm", {
                                    who:
                                        assignmentToRemove.userName ||
                                        assignmentToRemove.email,
                                })}
                            </p>
                        </div>
                    )
                }
                footer={
                    <>
                        <CapsuleButton
                            text={t("global_cancel")}
                            onClick={() => setAssignmentToRemove(null)}
                        />
                        <CapsuleButton
                            style="DANGER"
                            text={t("global_remove")}
                            onClick={() => {
                                if (assignmentToRemove) {
                                    handleRemoveAssignment(
                                        assignmentToRemove.email,
                                    );
                                }
                                setAssignmentToRemove(null);
                            }}
                        />
                    </>
                }
            />
            <CustomModal
                show={showDeleteConfirm}
                centered
                onHide={() => setShowDeleteConfirm(false)}
                title={t("mod_parking_delete")}
                content={
                    <div>
                        <p>
                            {t("mod_parking_delete_confirm", {
                                number: spot.number,
                            })}
                        </p>
                        <CaptionText className="font-italic">
                            {t("global_cannot_undo")}
                        </CaptionText>
                    </div>
                }
                footer={
                    <>
                        <CapsuleButton
                            text={t("global_cancel")}
                            onClick={() => setShowDeleteConfirm(false)}
                        />
                        <CapsuleButton
                            style="DANGER"
                            text={t("mod_parking_delete")}
                            onClick={handleDelete}
                        />
                    </>
                }
            />
        </>
    );
}
